import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { optimizeLogoImage } from "../../utils/optimizeImage";

interface LandingHeaderProps {
  countyName: string | null;
  countyLogo: string | null;
  additionalLogos: (string | null)[];
  dashboardUrl: string;
}

export default function LandingHeader({ countyName, countyLogo, additionalLogos, dashboardUrl }: LandingHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Get the scroll position from the landing page container
      const landingContainer = document.querySelector('.h-screen.overflow-y-auto');
      const scrollY = landingContainer?.scrollTop || 0;
      setIsScrolled(scrollY > 50);
    };

    const landingContainer = document.querySelector('.h-screen.overflow-y-auto');
    if (landingContainer) {
      landingContainer.addEventListener("scroll", handleScroll);
      return () => landingContainer.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const navItems = [
    { label: "About", href: "#about" },
    { label: "Impact", href: "#impact" },
    { label: "Partners", href: "#partners" },
  ];

  // Filter out null logos
  const validLogos = additionalLogos.filter((logo) => logo !== null);

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/90 backdrop-blur-md shadow-lg"
          : "bg-black/20 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logos Section */}
          <div className="flex items-center gap-3 sm:gap-4">
            {/* County Logo */}
            {countyLogo && (
              <img
                src={optimizeLogoImage(countyLogo)}
                alt={countyName || "County"}
                width={120}
                height={48}
                className={`h-10 sm:h-12 w-auto max-w-[100px] sm:max-w-[120px] object-contain ${!isScrolled ? 'drop-shadow-lg' : ''}`}
              />
            )}

            {/* Additional Logos */}
            {validLogos.map((logo, index) => (
              <img
                key={index}
                src={optimizeLogoImage(logo)}
                alt={`Partner ${index + 1}`}
                width={120}
                height={48}
                className={`h-10 sm:h-12 w-auto max-w-[100px] sm:max-w-[120px] object-contain hidden md:block ${!isScrolled ? 'drop-shadow-lg' : ''}`}
              />
            ))}

            {/* M-Taka Logo - Now last */}
            <img
              src="/logo.webp"
              alt="M-Taka"
              width={120}
              height={48}
              className={`h-10 sm:h-12 w-auto max-w-[100px] sm:max-w-[120px] object-contain ${!isScrolled ? 'drop-shadow-lg' : ''}`}
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className={`font-medium transition-colors ${
                  isScrolled ? "text-gray-700 hover:text-primary" : "text-white hover:text-primary drop-shadow-md"
                }`}
              >
                {item.label}
              </a>
            ))}

            <a
              href={dashboardUrl}
              className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover transition-colors"
            >
              Dashboard
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 ${
              isScrolled ? "text-gray-900" : "text-white"
            }`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden mt-4 pt-4 border-t border-gray-200">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className={`block py-2 font-medium transition-colors ${
                  isScrolled ? "text-gray-700 hover:text-primary" : "text-white hover:text-primary drop-shadow-md"
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </a>
            ))}
            <a
              href={dashboardUrl}
              className="block mt-4 px-6 py-2 bg-primary text-white rounded-lg font-medium text-center hover:bg-primary-hover transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Dashboard
            </a>
          </nav>
        )}
      </div>
    </header>
  );
}
